/**
 * SharedLoginPage — unified login for both Lite (plan 1) and Pro (plan 2).
 * After a successful login the JWT token is decoded to read the `plan` field:
 *   plan === 2  →  redirect to /brand  (Pro CMS)
 *   plan === 1  →  redirect to /lite   (Lite CMS)  [default if field missing]
 *
 * The original per-mode pages are preserved as *.bak.js backups.
 */

import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { connect } from "react-redux";
import {
  logout,
  loadUserData,
  requestServerPost,
  requestServerGet,
  loadAccounts,
  selectPage,
  requestServerPostSocialLogin,
} from "../actions";
import "./login_lite.css";
import { Form, Button, Checkbox, Input, message, Spin, Modal } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import {
  MonitorPlay,
  CalendarClock,
  BarChart2,
  Megaphone,
  Layers,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import md5 from "js-md5";
import Constants from "../data/constants";
import { ENABLE_ACCOUNT_SWITCHING, getLogInAPIAction } from "../Config";
import PopupManager from "../managers/PopupManager";
import { signInWithGoogle } from "./lite/googleAuth";
import DataHelper from "../helpers/DataHelper";
import StringHelper from "../helpers/StringHelper";
import ChangePasswordModal from "../components/popups/ChangePasswordModal";

// ── Feature list (plan-neutral) ───────────────────────────────────
const FEATURES = [
  {
    icon: MonitorPlay,
    title: "Manage Screens",
    desc: "Control all your displays from one centralised dashboard.",
  },
  {
    icon: Layers,
    title: "Multi-Zone Layouts",
    desc: "Design flexible layouts with full creative control.",
  },
  {
    icon: CalendarClock,
    title: "Smart Scheduling",
    desc: "Schedule content by day, time, and event trigger.",
  },
  {
    icon: BarChart2,
    title: "Analytics & Reports",
    desc: "Monitor uptime, content plays, and audit logs.",
  },
  {
    icon: Megaphone,
    title: "Campaign Management",
    desc: "Push targeted campaigns across all your locations.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

// ── Redirect helper ───────────────────────────────────────────────
function redirectByPlan(token, historyHook, selectPage) {
  const plan = StringHelper.getJWTPlan(token);
  if (plan === 2) {
    selectPage(Constants.BRAND_PAGE);
    historyHook.push(`/${Constants.BRAND_PAGE}`);
  } else {
    historyHook.push("/lite");
  }
}

// ── Component ─────────────────────────────────────────────────────
function SharedLoginPage(props) {
  const [loading, setLoading] = useState(false);
  const [openGuestChoiceModal, setOpenGuestChoiceModal] = useState(false);
  const [isGuestExpired, setIsGuestExpired] = useState(false);
  const [guestChoiceMessage, setGuestChoiceMessage] = useState(
    "You are logging in as a Guest User, would you like to activate your account?",
  );
  const {
    requestServerPost,
    requestServerGet,
    loadAccounts,
    loadUserData,
    selectPage,
    userdata,
    logout,
    usertoken,
  } = props;
  const historyHook = useHistory();
  const usernameRef = useRef("");
  const passwordRef = useRef("");
  const [messageApi, contextHolder] = message.useMessage();
  const [guestUser, setGuestUser] = useState(null);
  const [tempToken, setTempToken] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [tempPass, setTempPass] = useState("");

  useEffect(() => {
    DataHelper.logoutInProgress = false;
  }, []);

  const onFinish = (values) => {
    const { username, password } = values;
    const hash = md5(username + "" + md5(password));
    setLoading(true);
    const loginAction = getLogInAPIAction();

    requestServerPost(loginAction, { hash }, {}, (response) => {
      if (response.data.token != null) {
        setLoading(false);

        // Fetch accounts for account-switcher
        const loggedInUserId = response.data.user && response.data.user.userid;
        if (loggedInUserId && ENABLE_ACCOUNT_SWITCHING) {
          try {
            requestServerGet(
              "get_accountbyuser",
              { userid: loggedInUserId },
              {},
              (accountsResponse) => {
                const accounts = accountsResponse?.data?.data || [];
                loadAccounts(accounts);
              },
              response.data.token,
            );
          } catch (error) {
            console.log("Error fetching accounts:", error);
          }
        }

        // Guest user flow
        if ("is_guest" in response.data.user && response.data.user.is_guest) {
          setGuestUser(response.data.user);
          setTempToken(response.data.token);
          if (
            "is_expired" in response.data.user &&
            response.data.user.is_expired
          ) {
            setIsGuestExpired(true);
            setGuestChoiceMessage(
              "Your guest account has expired. Please activate your account to continue.",
            );
          } else {
            setIsGuestExpired(false);
            setGuestChoiceMessage(
              "You are logging in as a Guest User, would you like to activate your account?",
            );
          }
          setOpenGuestChoiceModal(true);
          return;
        }

        // Temporary password flow
        if (response.data.user && response.data.user.istemppass) {
          setTempPass(password);
          setShowChangePassword(true);
          return;
        }

        // Normal redirect — driven by JWT plan
        loadUserData(response.data);
        redirectByPlan(response.data.token, historyHook, selectPage);
      } else {
        message.error("Invalid Username or Password");
      }
      setLoading(false);
    });
  };

  const handleForgotPassword = () => {
    PopupManager.launchForgotPasswordPopup();
  };

  const onGoogleLogin = async () => {
    setLoading(true);
    try {
      const { idToken, email, displayName, photoURL, social_channel, uid } =
        await signInWithGoogle();
      props.requestServerPostSocialLogin(
        "social_auth",
        { idToken, email, displayName, photoURL, social_channel, uid },
        "google",
        (response) => {
          setLoading(false);
          if (response?.data?.token) {
            loadUserData(response.data);
            redirectByPlan(response.data.token, historyHook, selectPage);
          } else {
            message.error("Google login failed. Please try again.");
          }
        },
        "",
        (err) => {
          setLoading(false);
          message.error("Google sign-in failed. Please try again.");
        },
      );
    } catch (err) {
      setLoading(false);
      if (err.code === "auth/account-exists-with-different-credential") {
        message.warning(
          err.message ||
            "This email is already registered with another provider.",
        );
      } else if (err.code !== "auth/popup-closed-by-user") {
        message.error("Google sign-in failed. Please try again.");
      }
    }
  };

  const onFormValueChange = ({ username, password }) => {
    if (username !== undefined) usernameRef.current.value = username;
    if (password !== undefined) passwordRef.current.value = password;
  };

  return (
    <div
      className="lite-page"
      style={{ fontFamily: "'Kanit', 'Inter', -apple-system, sans-serif" }}
    >
      {loading && (
        <div
          style={{
            position: "fixed",
            width: "100%",
            height: "100vh",
            backgroundColor: "black",
            opacity: 0.35,
            zIndex: 1000,
            top: 0,
            left: 0,
          }}
        />
      )}
      {loading && (
        <div
          style={{
            position: "fixed",
            width: "100%",
            height: "100vh",
            zIndex: 1001,
            top: 0,
            left: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" tip="Loading..." style={{ color: "white" }} />
        </div>
      )}

      <ChangePasswordModal
        user={userdata?.user}
        usertoken={usertoken}
        requestServerPost={requestServerPost}
        currentPassword={tempPass}
        visible={showChangePassword}
        onCancel={() => setShowChangePassword(false)}
        onComplete={() => {
          setShowChangePassword(false);
          logout();
        }}
      />
      {contextHolder}

      {/* ── Left panel ── */}
      <div
        className="lite-left"
        style={{
          position: "relative",
          background:
            "linear-gradient(160deg, #06061a 0%, #0e0e2e 40%, #0a0a20 70%, #06061a 100%)",
          overflow: "hidden",
        }}
      >
        {/* Glows */}
        <div
          style={{
            position: "absolute",
            width: "560px",
            height: "560px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 65%)",
            top: "-140px",
            left: "-180px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 65%)",
            bottom: "-100px",
            right: "-100px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%)",
            top: "45%",
            left: "55%",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            pointerEvents: "none",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lite-left-inner"
        >
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="initial"
            animate="animate"
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 14px",
                borderRadius: "20px",
                background: "rgba(99,102,241,0.12)",
                border: "1px solid rgba(99,102,241,0.28)",
                marginBottom: "28px",
              }}
            >
              <span
                style={{
                  color: "#a5b4fc",
                  fontSize: "13px",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                }}
              >
                ✦ CodexSignage
              </span>
            </div>
          </motion.div>

          <motion.h1
            custom={1}
            variants={fadeUp}
            initial="initial"
            animate="animate"
            style={{
              fontSize: "clamp(38px, 4.5vw, 60px)",
              fontWeight: 800,
              color: "#ffffff",
              margin: "0 0 16px",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
            }}
          >
            Digital Signage
            <br />
            <span
              style={{
                background:
                  "linear-gradient(135deg, #818cf8 0%, #6366f1 40%, #4f46e5 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Made Simple.
            </span>
          </motion.h1>

          <motion.p
            custom={2}
            variants={fadeUp}
            initial="initial"
            animate="animate"
            style={{
              fontSize: "clamp(14px, 1.8vw, 16px)",
              color: "rgba(255,255,255,0.5)",
              margin: "0 0 32px",
              lineHeight: 1.7,
              maxWidth: "400px",
            }}
          >
            Manage screens, playlists, campaigns, and schedules — from one
            powerful platform that scales with your business.
          </motion.p>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  custom={i + 3}
                  variants={fadeUp}
                  initial="initial"
                  animate="animate"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    background: "rgba(99,102,241,0.07)",
                    border: "1px solid rgba(99,102,241,0.15)",
                  }}
                >
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "8px",
                      background: "rgba(99,102,241,0.2)",
                      border: "1px solid rgba(99,102,241,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={16} color="#a5b4fc" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#f1f5f9",
                        marginBottom: "1px",
                      }}
                    >
                      {f.title}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "rgba(255,255,255,0.38)",
                        lineHeight: 1.4,
                      }}
                    >
                      {f.desc}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            custom={9}
            variants={fadeUp}
            initial="initial"
            animate="animate"
            style={{
              marginTop: "24px",
              fontSize: "11px",
              color: "rgba(255,255,255,0.2)",
              letterSpacing: "0.06em",
              textAlign: "center",
            }}
          >
            TRUSTED BY SIGNAGE PROFESSIONALS WORLDWIDE
          </motion.div>
        </motion.div>
      </div>

      {/* ── Right panel: login form ── */}
      <div className="lite-right">
        <motion.div
          className="lite-right-inner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <img
            src="/cms/assets/svgs/cdx_logo.svg"
            alt="CodexSignage"
            style={{
              height: "52px",
              objectFit: "contain",
              marginBottom: "24px",
            }}
          />

          <h2
            style={{
              fontSize: "clamp(20px, 4vw, 28px)",
              fontWeight: 700,
              color: "#0f172a",
              margin: "0 0 6px",
              letterSpacing: "-0.02em",
            }}
          >
            Welcome back
          </h2>
          <p style={{ fontSize: "14px", color: "#94a3b8", margin: "0 0 20px" }}>
            Sign in to your account
          </p>

          {/* Google sign-in */}
          <button
            type="button"
            disabled={loading}
            onClick={onGoogleLogin}
            className="lite-social-btn"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "10px 16px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              background: "#fff",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: 15,
              fontWeight: 500,
              color: "#0f172a",
              marginBottom: 16,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 48 48"
              style={{ flexShrink: 0 }}
            >
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.05 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.55-13.46-8.9l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            Continue with Google
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 20,
              width: "100%",
            }}
          >
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            <span
              style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}
            >
              or sign in with email
            </span>
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
          </div>

          <Form
            name="shared_login"
            initialValues={{ remember: true }}
            onValuesChange={onFormValueChange}
            onFinish={onFinish}
            style={{ width: "100%" }}
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: "Please enter your username" },
              ]}
            >
              <Input
                ref={usernameRef}
                prefix={<UserOutlined style={{ color: "#cbd5e1" }} />}
                placeholder="Username"
                size="large"
                style={{
                  borderRadius: "10px",
                  fontSize: "15px",
                  border: "1px solid #e2e8f0",
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <Input.Password
                ref={passwordRef}
                prefix={<LockOutlined style={{ color: "#cbd5e1" }} />}
                placeholder="Password"
                size="large"
                style={{
                  borderRadius: "10px",
                  fontSize: "15px",
                  border: "1px solid #e2e8f0",
                }}
              />
            </Form.Item>

            <Form.Item>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox style={{ color: "#64748b", fontSize: "13px" }}>
                  Remember me
                </Checkbox>
              </Form.Item>
              <a
                onClick={handleForgotPassword}
                style={{
                  float: "right",
                  color: "#6366f1",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                Forgot password
              </a>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                className="lite-primary-btn"
                style={{
                  borderRadius: "10px",
                  height: "48px",
                  fontWeight: 600,
                  fontSize: "16px",
                  background:
                    "linear-gradient(90deg, #4f46e5, #6366f1, #7c3aed)",
                  border: "none",
                  boxShadow: "0 4px 16px rgba(79,70,229,0.4)",
                }}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>

          <p
            style={{
              marginTop: 20,
              fontSize: 13,
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            Don't have an account?{" "}
            <span
              style={{ color: "#6366f1", cursor: "pointer", fontWeight: 600 }}
              onClick={() => historyHook.push("/register")}
            >
              Create account
            </span>
          </p>
        </motion.div>
      </div>

      {/* ── Guest user modal ── */}
      <Modal
        title={guestChoiceMessage}
        footer={null}
        width="min(400px, 90vw)"
        bodyStyle={{ padding: "20px", textAlign: "center" }}
        centered
        open={openGuestChoiceModal}
        onOk={() => setOpenGuestChoiceModal(false)}
        onCancel={() => setOpenGuestChoiceModal(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {!isGuestExpired ? (
            <div
              style={{ display: "flex", justifyContent: "center", gap: "10px" }}
            >
              <Button
                onClick={() => {
                  setOpenGuestChoiceModal(false);
                  selectPage(Constants.BRAND_PAGE);
                }}
              >
                No, I'll do it later
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  setOpenGuestChoiceModal(false);
                  selectPage(Constants.GUEST_ACTIVATION_PAGE);
                }}
              >
                Yes, Activate
              </Button>
            </div>
          ) : (
            <>
              <Button
                type="primary"
                style={{ width: "100%" }}
                onClick={() => {
                  setOpenGuestChoiceModal(false);
                  selectPage(Constants.GUEST_ACTIVATION_PAGE);
                }}
              >
                Activate with email
              </Button>
              <Button
                style={{ width: "100%" }}
                onClick={() => {
                  setLoading(true);
                  const next7Days = new Date();
                  next7Days.setDate(next7Days.getDate() + 7);
                  const formatted_end_date = `${next7Days.getFullYear()}-${next7Days.getMonth() + 1}-${next7Days.getDate()}`;
                  const params = new FormData();
                  params.append("userid", guestUser?.userid);
                  params.append("end_date", formatted_end_date);
                  requestServerPost(
                    "update_user",
                    params,
                    {},
                    (response) => {
                      setLoading(false);
                      if (response?.data?.status === "success") {
                        DataHelper.nextSessionOverMessage =
                          "Guest account extended successfully. Please login again.";
                        setOpenGuestChoiceModal(false);
                        selectPage(Constants.BRAND_PAGE);
                      } else {
                        message.error(
                          response?.data?.message ||
                            "Failed to extend guest account",
                        );
                      }
                    },
                    tempToken,
                  );
                }}
              >
                Extend for 7 days
              </Button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  usertoken: state.usertoken,
  userdata: state.userdata,
});

export default connect(mapStateToProps, {
  logout,
  loadUserData,
  selectPage,
  requestServerPost,
  requestServerGet,
  requestServerPostSocialLogin,
  loadAccounts,
})(SharedLoginPage);
